import {
	ChangeEvent,
	Dispatch,
	FunctionComponent,
	SetStateAction,
	useEffect,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { useUpdateColumnTemplateMutation } from "@/pages/Private/redux/column/column.api";
import {
	ColumnTemplate,
	UpdateColumnTemplate,
	UpdateColumnTemplateSchema,
} from "@/pages/Private/pages/List/schema/column-template";
import { InputField } from "@/components/InputField/InputField";
import { PromptEditor } from "@/pages/Private/pages/Messages/components/AiSnippets/components/PromptEditor";
import { Radio } from "@/components/Radio/Radio";
import { AllowedValuesBySchema } from "@/pages/Private/pages/Messages/components/AiSnippets/components/AllowedValuesBySchema";
import { AiSnippetEnum } from "@/enum/ai-enrichment.enum";

interface UpdatePromptModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	prompt: ColumnTemplate;
}

export const UpdatePromptModal: FunctionComponent<UpdatePromptModalProps> = ({
	isOpen,
	setIsOpen,
	prompt,
}) => {
	const { t } = useTranslation();
	const [updateColumnTemplate, { isLoading, error }] = useUpdateColumnTemplateMutation();

	const [editorValue, setEditorValue] = useState<string>();

	useEffect(() => {
		if (prompt) {
			setEditorValue(prompt?.config?.prompt ? prompt?.config?.prompt : "");
		}
	}, [prompt]);

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<UpdateColumnTemplate>({
		defaultValues: {
			id: prompt.id,
			name: prompt.name,
			config: prompt.config,
			type: prompt.type,
		},
		resolver: zodResolver(UpdateColumnTemplateSchema),
	});

	const formValues = watch();

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: UpdateColumnTemplate) => {
		try {
			values.config = {
				...values.config,
				prompt: editorValue || "",
			};

			await updateColumnTemplate({
				...values,
				config: JSON.stringify(values.config),
			}).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const handleAddAllowedValue = (value: string) => {
		setValue("config", {
			...formValues.config,
			allowedValues: [...(formValues.config?.allowedValues || []), value],
		});
	};

	const handleRemoveAllowedValue = (value: string) => {
		setValue(
			"config",
			{
				...formValues.config,
				allowedValues: formValues.config?.allowedValues?.filter((v: string) => v !== value),
			},
			{
				shouldValidate: true,
				shouldDirty: true,
			}
		);
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			size="lg"
			submitButtonText={t("basics.confirm")}
			title={"Edit Prompt Template"}
		>
			<div className="flex flex-row w-full gap-8">
				<div className="w-1/2">
					<div className="flex items-center mb-1">
						<InputField
							error={!!errors.name?.message}
							handleChange={(e: ChangeEvent<HTMLInputElement>) => {
								setValue("name", e.target.value);
							}}
							label={"Name"}
							name={"name"}
							placeholder={"e.g. My Prompt Template"}
							value={formValues.name}
						/>
					</div>
					<div className="flex flex-col w-full">
						<PromptEditor
							currentLeadIndex={0}
							handleChange={(value: string) => {
								setEditorValue(value);
							}}
							value={editorValue || undefined}
						/>
					</div>
				</div>
				<div className="w-1/2">
					<div className="text-sm font-semibold mb-4">How should the AI agent respond?</div>
					<div className="flex flex-col w-full gap-2">
						<Radio
							isChecked={formValues.config?.promptType === AiSnippetEnum.BEST_RESPONSE}
							onChange={() =>
								setValue("config", {
									...formValues.config,
									promptType: AiSnippetEnum.BEST_RESPONSE,
								})
							}
						>
							The AI agent should come up with the best response
						</Radio>
						<Radio
							isChecked={formValues.config?.promptType === AiSnippetEnum.SCHEMA}
							onChange={() =>
								setValue("config", { ...formValues.config, promptType: AiSnippetEnum.SCHEMA })
							}
						>
							<span className="text-sm text-text-meta w-fit flex">
								The AI agent should only write one of the following values
							</span>
						</Radio>
					</div>
					{formValues.config?.promptType === AiSnippetEnum.SCHEMA && (
						<AllowedValuesBySchema
							allowedValues={formValues.config?.allowedValues || []}
							onAdd={handleAddAllowedValue}
							onRemove={handleRemoveAllowedValue}
						/>
					)}
					{renderErrorMessages(getAllErrors(error, formErrors))}
				</div>
			</div>
		</Modal>
	);
};
